import React, { useRef, useState, useEffect } from 'react';
import { ContentRow } from '../../../types/homepage';

interface MediumCarouselProps {
    row: ContentRow;
    onVideoClick?: (videoId: string) => void;
}

const MediumCarousel: React.FC<MediumCarouselProps> = ({ row, onVideoClick }) => {
    const { content, title, subtitle, settings } = row;
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const update = () => {
            setCanLeft(el.scrollLeft > 4);
            setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        return () => el.removeEventListener('scroll', update);
    }, [content]);

    if (!content?.length) return null;

    const scroll = (dir: 'left' | 'right') => {
        scrollRef.current?.scrollBy({ left: dir === 'right' ? 320 : -320, behavior: 'smooth' });
    };

    const isCourseRow = content.some((item: any) =>
        item.cpd !== undefined || item.durationHrs !== undefined || item.type === 'course'
    );

    const handleView = (e: React.MouseEvent, item: any) => {
        e.stopPropagation();
        onVideoClick?.(item.id);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

                .mc-section { padding: 28px 0; background: var(--mc-bg, #f3f4f6); font-family: 'DM Sans', system-ui, sans-serif; }
                .mc-inner { max-width: 1440px; margin: 0 auto; padding: 0 24px; }
                .mc-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 16px; gap: 12px; }
                .mc-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -0.01em; }
                .mc-subtitle { font-size: 13.5px; color: #6b7280; margin: 0; }
                .mc-show-all { font-size: 14px; font-weight: 700; color: #111827; background: none; border: none; cursor: pointer; white-space: nowrap; padding: 0; transition: color 0.15s; flex-shrink: 0; font-family: inherit; }
                .mc-show-all:hover { color: #00897b; }
                .mc-wrap { position: relative; }
                .mc-track { display: flex; gap: 14px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; scroll-snap-type: x mandatory; }
                .mc-track::-webkit-scrollbar { display: none; }
                .mc-arrow { position: absolute; top: 38%; transform: translateY(-50%); width: 36px; height: 36px; border-radius: 50%; background: #ffffff; border: 1.5px solid #e5e7eb; color: #374151; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 5; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: background 0.15s, opacity 0.2s, color 0.15s; font-family: inherit; }
                .mc-arrow:hover { background: #00897b; color: #ffffff; border-color: #00897b; }
                .mc-arrow svg { width: 16px; height: 16px; }
                .mc-arrow[disabled] { opacity: 0; pointer-events: none; }
                .mc-arrow-l { left: -18px; }
                .mc-arrow-r { right: -18px; }
                .mc-card { flex: none; width: 280px; background: #ffffff; border-radius: 10px; overflow: hidden; border: 1px solid #e5e7eb; box-shadow: 0 1px 4px rgba(0,0,0,0.06); cursor: pointer; scroll-snap-align: start; transition: transform 0.2s, box-shadow 0.2s; }
                .mc-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
                .mc-thumb { position: relative; aspect-ratio: 16 / 9; background: #e5e7eb; overflow: hidden; }
                .mc-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.3s; }
                .mc-card:hover .mc-thumb img { transform: scale(1.04); }
                .mc-channel-tag { position: absolute; bottom: 6px; left: 7px; background: rgba(0,0,0,0.62); color: #fff; font-size: 10.5px; font-weight: 600; padding: 2px 8px; border-radius: 4px; font-family: inherit; max-width: 55%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .mc-cpd-badge { position: absolute; top: 7px; left: 7px; background: rgba(30,64,175,0.88); color: #fff; font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 4px; font-family: inherit; }
                .mc-price-badge { position: absolute; top: 7px; right: 7px; background: rgba(0,0,0,0.62); color: #fff; font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 4px; font-family: inherit; }
                .mc-dur { position: absolute; bottom: 6px; right: 7px; background: rgba(0,0,0,0.72); color: #fff; font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 4px; font-family: inherit; }
                .mc-live-badge { position: absolute; top: 7px; left: 7px; background: #e53935; color: #fff; font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 4px; display: flex; align-items: center; gap: 5px; font-family: inherit; }
                .mc-live-dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; animation: mc-pulse 1s ease-in-out infinite; flex-shrink: 0; }
                @keyframes mc-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
                .mc-body { padding: 11px 14px 13px; }
                .mc-type-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; margin: 0 0 3px; font-family: inherit; }
                .mc-video-title { font-size: 14px; font-weight: 700; color: #111827; margin: 0 0 5px; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-family: inherit; transition: color 0.15s; }
                .mc-card:hover .mc-video-title { color: #00897b; }
                .mc-views { font-size: 12.5px; color: #6b7280; margin: 0 0 8px; font-family: inherit; }
                .mc-course-meta { display: flex; align-items: center; justify-content: space-between; font-size: 12px; color: #6b7280; margin: 0 0 9px; font-family: inherit; }
                .mc-actions { display: flex; align-items: center; gap: 10px; }
                .mc-view-btn { display: inline-flex; align-items: center; padding: 7px 18px; background: #00897b; color: #fff; font-family: inherit; font-size: 12px; font-weight: 700; border-radius: 100px; border: none; cursor: pointer; letter-spacing: 0.04em; transition: filter 0.15s; white-space: nowrap; }
                .mc-view-btn:hover { filter: brightness(1.1); }
                .mc-price-label { font-size: 13px; font-weight: 700; color: #111827; font-family: inherit; margin-left: auto; }

                @media (max-width: 640px) {
                    .mc-inner { padding: 0 16px; }
                    .mc-card { width: 240px; }
                }
            `}</style>

            <section className="mc-section">
                <div className="mc-inner">
                    {Boolean(title || settings?.ctaLabel) && (
                        <div className="mc-header">
                            <div>
                                {title && <h2 className="mc-title">{title}</h2>}
                                {subtitle && <p className="mc-subtitle">{subtitle}</p>}
                            </div>
                            <button className="mc-show-all">{(settings?.ctaLabel as string) || 'Show All'}</button>
                        </div>
                    )}

                    <div className="mc-wrap">
                        <button className="mc-arrow mc-arrow-l" disabled={!canLeft} onClick={() => scroll('left')} aria-label="Scroll left">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <div className="mc-track" ref={scrollRef}>
                            {content.map((item: any) => {
                                const isPaid = item.price !== undefined && !item.isFree;
                                const isCourse = isCourseRow || item.type === 'course';
                                const durationLabel = isCourse && item.durationHrs !== undefined
                                    ? `${String(item.durationHrs).padStart(2, '0')} hrs ${String(item.durationMins ?? 0).padStart(2, '0')} mins`
                                    : null;

                                return (
                                    <div key={item.id} className="mc-card" onClick={() => onVideoClick?.(item.id)}>
                                        <div className="mc-thumb">
                                            {item.thumbnail && <img src={item.thumbnail} alt={item.title} loading="lazy" />}
                                            {isCourse && item.cpd !== undefined && <span className="mc-cpd-badge">CPD: {item.cpd}</span>}
                                            {isPaid && <span className="mc-price-badge">${item.price.toFixed(2)}</span>}
                                            {!isCourse && item.channelName && <span className="mc-channel-tag">{item.channelName}</span>}
                                            {!isCourse && item.duration && <span className="mc-dur">{item.duration}</span>}
                                            {item.isLive && <span className="mc-live-badge"><span className="mc-live-dot" /> LIVE</span>}
                                        </div>

                                        <div className="mc-body">
                                            {isCourse && <p className="mc-type-label">Course</p>}
                                            <h3 className="mc-video-title">{item.title}</h3>

                                            {!isCourse && item.views && <p className="mc-views">{(item.views as number).toLocaleString()} views</p>}
                                            {isCourse && (
                                                <div className="mc-course-meta">
                                                    {durationLabel && <span>Duration: {durationLabel}</span>}
                                                    {item.cpd !== undefined && <span>CPD: {item.cpd.toFixed(1)}</span>}
                                                </div>
                                            )}

                                            <div className="mc-actions">
                                                <button className="mc-view-btn" onClick={(e) => handleView(e, item)}>
                                                    {isCourse ? 'Start Course' : 'View'}
                                                </button>
                                                {isPaid && <span className="mc-price-label">${item.price.toFixed(2)}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button className="mc-arrow mc-arrow-r" disabled={!canRight} onClick={() => scroll('right')} aria-label="Scroll right">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
};

export default MediumCarousel;